/** 表示の言葉と数字の整形（画面ごとに言い方を変えない）。 */
import { atwaterKcal } from "@kalori/nutrition/quality";
import type { CatalogState, FeedbackStatus, NutritionKey, ReviewState, SubmitWarning } from "./api";

export const NUTRITION_FIELDS: { key: NutritionKey; label: string; unit: string }[] = [
  { key: "cal", label: "エネルギー", unit: "kcal" },
  { key: "protein", label: "たんぱく質", unit: "g" },
  { key: "fat", label: "脂質", unit: "g" },
  { key: "carbs", label: "炭水化物", unit: "g" },
  { key: "sugar", label: "糖質", unit: "g" },
  { key: "fiber", label: "食物繊維", unit: "g" },
  { key: "salt", label: "食塩相当量", unit: "g" },
];

/**
 * 項目ごとの由来（`nutrition_source_map` の語彙）→ 事業者に見せる言葉。
 * ⚠️ 「公式」は御社サイトから kalori が読み取った値・「御社入力」は承認済みの公式値。混ぜない。
 */
export function sourceLabel(source: string | null): { text: string; tone: "official" | "estimated" | "operator" | "unknown" } {
  switch (source) {
    case "official":
      return { text: "公式サイト", tone: "official" };
    case "operator":
      return { text: "御社入力", tone: "operator" };
    case "estimated":
      return { text: "AI 推定", tone: "estimated" };
    case "partial":
      return { text: "一部推定", tone: "estimated" };
    default:
      return { text: "未取得", tone: "unknown" };
  }
}

export const REVIEW_STATE_LABEL: Record<ReviewState, string> = {
  editing: "編集中",
  pending_review: "審査待ち",
  published: "公開済み",
  unpublished: "公開停止",
  archived: "保管",
};

/**
 * 紐付け先がいま載っているか（listed は何も言わない）。admin の公開中一覧と同じ判定（`@kalori/business/brands`）。
 * ⚠️ 「削除された」と言い切らない — 改名で別の行に置き換わっただけのことが多い。
 */
export const CATALOG_STATE_LABEL: Record<Exclude<CatalogState, "listed">, { chip: string; text: string }> = {
  leaving: {
    chip: "公式サイトで見当たらない",
    text: "このメニューが御社サイトで見当たらなくなっています。数日続くと kalori の掲載から外れます（サイトの一時的な変化なら元に戻ります）。",
  },
  gone: {
    chip: "掲載から外れた",
    text: "このメニューは kalori の掲載から外れたため、入力した公式値はいま誰にも表示されていません。名前が変わった場合は、掲載中のメニューから新しい品を選んで入力し直してください。",
  },
};

export const SUBMIT_WARNING_LABEL: Record<SubmitWarning, string> = {
  kcal_pfc_mismatch: "エネルギーと、たんぱく質・脂質・炭水化物から計算した値が 30% 以上ずれています。桁や単位をご確認ください。",
  kcal_not_verifiable: "たんぱく質・脂質・炭水化物のどれかが空欄のため、エネルギーを検算できません。",
  no_kcal: "エネルギーが空欄です。カタログの主な表示が推定値のまま残ります。",
};

export const SUBMIT_BLOCKER_LABEL: Record<string, string> = {
  already_published: "公開済みです。",
  already_submitted: "すでに申請済みです。",
  not_linked_to_catalog_product: "kalori に掲載中のメニューと紐付いていません。",
  linked_product_gone: "紐付けたメニューが kalori の掲載から外れています。名前が変わった場合は、掲載中のメニューから新しい品を選んで入力し直してください。",
  no_nutrition_values: "栄養成分も写真も入っていません（どちらか 1 つは必要です）。",
  not_editable: "この状態からは申請できません。",
  no_value_basis: "値の根拠を選んでください。",
  terms_not_accepted: "利用規約への同意が必要です。",
};

export function fmtNumber(v: number | null | undefined, unit = ""): string {
  if (v == null) return "—";
  const s = Number.isInteger(v) ? v.toLocaleString("ja-JP") : v.toLocaleString("ja-JP", { maximumFractionDigits: 1 });
  return unit ? `${s} ${unit}` : s;
}

export function fmtYen(v: number | null): string {
  return v == null ? "—" : `${v.toLocaleString("ja-JP")} 円`;
}

/** D1 の `YYYY-MM-DD HH:MM:SS`（UTC）/ ISO → 日本時間の `YYYY/MM/DD HH:MM`。 */
export function fmtDateTime(s: string | null): string {
  if (!s) return "—";
  const t = Date.parse(s.includes("T") ? s : `${s.replace(" ", "T")}Z`);
  if (!Number.isFinite(t)) return s;
  return new Date(t).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** 4P + 9F + 4C（3 つ揃わなければ null — 部分和は誤読される。サーバの derivePfcCal と同じ規則）。 */
export function pfcDerivedCal(protein: number | null, fat: number | null, carbs: number | null): number | null {
  if (protein == null || fat == null || carbs == null) return null;
  return Math.round(atwaterKcal({ protein, fat, carbs }));
}

/** お問い合わせの状態（事業者から見た言い方）。 */
export const FEEDBACK_STATUS_LABEL: Record<FeedbackStatus, string> = {
  open: "運営が確認中",
  responded: "運営から返信あり",
  closed: "完了",
};
